import { Box, CircularProgress, Grid } from "@mui/material";

import { useForm } from "react-hook-form";

import { RoundButton } from "../../../../../_styles/StyledButtons";
import { yupResolver } from "@hookform/resolvers/yup";
import { followUpFormSchema } from "../../../../../_validators/schemas/followUpForm.schema";
import { useStore } from "../../../../../stores/store";
import { FollowUpType } from "../../../../../data/models/session";
import FormInputSelect from "../../../../../components/form/FormInputSelect";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { followUpOptions } from "../../../../../data/static/followupOptions";
import { useEffect, useState } from "react";

interface FollowUpFormProps {
    sessionId: string;
}

function FollowUpForm({ sessionId }: FollowUpFormProps) {
    const { t } = useTranslation();
    const { sessionStore, modalStore, loadingStore } = useStore();
    const { setFollowUpType, loadAssessorSession, getTimelineEvents, session } = sessionStore;
    const { closeModal } = modalStore;
    const { handleSubmit, control, watch, reset } = useForm({
        resolver: yupResolver(followUpFormSchema()),
        defaultValues: {
            followUp: FollowUpType.ONLINE,
        },
    });
    const { isLoading } = loadingStore
    const type = watch("followUp");
    const [loading, setLoading] = useState(true);

    const onSubmit = async (data: any) => {
        await setFollowUpType(sessionId, data.followUp);
        await loadAssessorSession(sessionId);
        closeModal();
    };

    const [hasExistingFollowupEvents, setHasExistingFollowupEvents] =
        useState<boolean>(false);


    useEffect(() => {
        if (sessionId) {
            loadAssessorSession(sessionId).then(() => {
                if (session?.timelineId) {
                    const fetchData = async () => {
                        const events = await getTimelineEvents(
                            sessionId,
                            session.timelineId
                        );

                        setHasExistingFollowupEvents(events.length > 0);
                    };
                    fetchData().then(() => setLoading(false));
                }

                // session is loaded, reset the form with the session data
                if (session?.assessmentType !== FollowUpType.ONLINE) {
                    reset({
                        followUp: session?.assessmentType,
                    });
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return <CircularProgress />
    }

    return (
        <Box>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container>
                    <Grid item xs={12} sx={{ mb: "1rem" }}>
                        <FormInputSelect
                            options={followUpOptions}
                            name={"followUp"}
                            control={control}
                            label={t("FOLLOWUP_TITLE", { timeline: session?.timeline ? `for ${session.timeline}` : "" })}
                            labelKey={"label"}
                            valueKey={"value"}
                            helperText={hasExistingFollowupEvents && type === FollowUpType.ONLINE ? t('CHANGE_FOLLOWUP_EXISTING_EVENT') : ''}
                        />
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <RoundButton variant="contained" type="submit" disabled={isLoading(setFollowUpType) || session?.assessmentType === type || (hasExistingFollowupEvents && type === FollowUpType.ONLINE)}>
                            {isLoading(setFollowUpType) ?
                                <CircularProgress size={25} />
                                :
                                t("GENERAL_SUBMIT")
                            }
                        </RoundButton>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default observer(FollowUpForm);
